/**
 * useBuildRequestDetail — detail + edit viewmodel for the operator's
 * build-request modal.
 *
 * Fetches the header (header DTO matches the list row) + items, and
 * exposes three mutations:
 *   - patchMeta:      PATCH /order-management/build-requests/:id
 *                     { status?, organisation_id?, max_allowed_count? }
 *   - replaceItems:   PATCH /order-management/build-requests/:id/items
 *                     { items: [...] } — full sync, the server rewrites
 *                     and recomputes item_count.
 *   - decommission:   POST  /order-management/build-requests/:id/decommission
 *                     Detaches the tenant CF custom domain; D1 state
 *                     preserved (audit log is the only artifact).
 *
 * Publish (POST /:id/build) moved to the list VM's inline action
 * (Kiran 2026-06-08) — it never lived on the modal again so the detail
 * VM stops carrying it.
 *
 * On every successful mutation we refresh the local detail from the
 * server's response so the modal renders authoritative state.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import type { BuildRequestListDto } from './useOrderManagementBuildRequests'

export interface BuildRequestItemDto {
  readonly id: string
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly quantity: number
}

export interface BuildRequestDetailDto extends BuildRequestListDto {
  readonly items: ReadonlyArray<BuildRequestItemDto>
}

export interface BuildRequestItemReplaceInput {
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly quantity: number
}

interface DetailBody {
  readonly success: true
  readonly data: BuildRequestDetailDto
}

export function useBuildRequestDetail(id: string | null): {
  readonly detail: BuildRequestDetailDto | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly patchMeta: (patch: {
    readonly status?: string
    readonly organisation_id?: string
    readonly max_allowed_count?: number | null
    readonly max_cart_value_paisa?: number | null
  }) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly replaceItems: (items: ReadonlyArray<BuildRequestItemReplaceInput>) => Promise<
    { ok: true } | { ok: false; message: string }
  >
  /** Decommission: detach the CF custom domain so the URL stops
   *  responding. D1 state stays untouched (status='built' remains,
   *  subdomain stays bound). Returns the unchanged detail row on
   *  success so the UI re-renders with the same values + a toast. */
  readonly decommission: () => Promise<{ ok: true } | { ok: false; message: string }>
} {
  const { authedFetch } = useAuth()
  const [detail, setDetail] = useState<BuildRequestDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(id !== null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (id === null) {
      setDetail(null)
      setError(null)
      setIsLoading(false)
      return
    }
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTITEM, { id }),
          { signal: ac.signal }
        )
        if (cancelled) return
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as DetailBody
        if (!cancelled) setDetail(body.data)
      } catch (e) {
        if (!cancelled && (e as { name?: string }).name !== 'AbortError') {
          setError(e instanceof Error ? e.message : 'Could not load build request')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [id, authedFetch, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])

  const patchMeta = useCallback(
    async (patch: {
      readonly status?: string
      readonly organisation_id?: string
      readonly max_allowed_count?: number | null
      readonly max_cart_value_paisa?: number | null
    }): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (id === null) return { ok: false, message: 'no build request loaded' }
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTITEM, { id }),
          { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        const body = (await res.json()) as DetailBody
        setDetail(body.data)
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Update failed' }
      }
    },
    [id, authedFetch]
  )

  const replaceItems = useCallback(
    async (items: ReadonlyArray<BuildRequestItemReplaceInput>): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (id === null) return { ok: false, message: 'no build request loaded' }
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTITEMS, { id }),
          { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        const body = (await res.json()) as DetailBody
        setDetail(body.data)
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Items update failed' }
      }
    },
    [id, authedFetch]
  )

  const decommission = useCallback(
    async (): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (id === null) return { ok: false, message: 'no build request loaded' }
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTDECOMMISSION, { id }),
          { method: 'POST' }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        const body = (await res.json()) as DetailBody
        setDetail(body.data)
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Decommission failed' }
      }
    },
    [id, authedFetch]
  )

  return { detail, isLoading, error, reload, patchMeta, replaceItems, decommission }
}
